import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export default function EvaluationAlerts() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [alerts, setAlerts] = useState([]);
  const shown = useRef(new Set());

  useEffect(() => {
    setAlerts([]);
    shown.current.clear();
    if (user?.role !== "student") return;
    let active = true;
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get("/evaluation-alerts");
        if (!active) return;
        setAlerts(data);
        for (const alert of data) {
          const notice = alert.evaluation_notice;
          if (!shown.current.has(notice.id)) {
            shown.current.add(notice.id);
            (alert.status === "evaluated" ? toast.success : toast.error)(notice.message);
            window.dispatchEvent(new Event("evaluation-updated"));
          }
        }
      } catch { /* Retry on the next poll after connectivity returns. */ }
      if (active) timer = setTimeout(poll, 5000);
    };
    poll();
    return () => { active = false; clearTimeout(timer); };
  }, [user?.id, user?.role]);

  const dismiss = async (alert) => {
    try {
      await api.post(`/evaluation-alerts/${alert.id}/${alert.evaluation_notice.id}/read`);
      setAlerts((current) => current.filter((item) => item.evaluation_notice.id !== alert.evaluation_notice.id));
    } catch { toast.error("Could not dismiss alert. Please try again."); }
  };

  if (!alerts.length || !user) return null;
  return (
    <aside aria-label="Evaluation notifications" className="fixed bottom-4 right-4 z-50 w-[calc(100%-2rem)] max-w-sm max-h-[50vh] overflow-y-auto space-y-2">
      {alerts.map((alert) => (
        <div key={alert.evaluation_notice.id} className="bg-background border border-border rounded-lg p-4 shadow-lg" role="status">
          <p className="text-sm font-medium">{alert.evaluation_notice.message}</p>
          <div className="flex gap-2 mt-3">
            <Button size="sm" onClick={() => { navigate(`/result/${alert.id}`); dismiss(alert); }}>
              {alert.status === "evaluated" ? "View result" : "Review and retry"}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => dismiss(alert)}>Dismiss</Button>
          </div>
        </div>
      ))}
    </aside>
  );
}
